/**
 * DoorListEditor - Component for editing multiple doors per wall
 *
 * Displays doors grouped by wall with add/remove functionality.
 * Supports multiple doors on the same wall.
 * Includes validation for overlapping doors and wall bounds.
 */

import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Plus, DoorOpen, AlertTriangle } from 'lucide-react'
import type { DoorSpec, DoorType, DoorWall, DoorsConfig } from '@/stores/buildingEditorStore'
import { validateWallDoors } from '@/lib/templates/single-unit-community'
import { DoorEditor } from './DoorEditor'

interface DoorListEditorProps {
  doors: DoorsConfig
  onChange: (doors: DoorsConfig) => void
  /** Width of the building - door-parallel dimension (for front/back walls) */
  widthFt: number
  /** Depth of the building - front-to-back (for left/right walls) */
  depthFt: number
  /** Height of the building (max door height) */
  heightFt: number
}

const WALL_LABELS: Record<DoorWall, string> = {
  front: 'Front Wall',
  back: 'Back Wall',
  left: 'Left Wall',
  right: 'Right Wall',
}

const WALL_ORDER: DoorWall[] = ['front', 'back', 'left', 'right']

export function DoorListEditor({
  doors,
  onChange,
  widthFt,
  depthFt,
  heightFt,
}: DoorListEditorProps) {
  /** Get wall length based on wall direction */
  const getWallLength = (wall: DoorWall): number => {
    return wall === 'front' || wall === 'back' ? widthFt : depthFt
  }

  /** Validate all walls and memoize results */
  const validationErrors = useMemo(() => {
    const errors: Record<DoorWall, string[]> = {
      front: [],
      back: [],
      left: [],
      right: [],
    }

    for (const wall of WALL_ORDER) {
      const wallLength = getWallLength(wall)
      const result = validateWallDoors(doors[wall], wallLength)
      errors[wall] = result.errors
    }

    return errors
  }, [doors, widthFt, depthFt])

  /** Check if any wall has errors */
  const hasErrors = useMemo(() => {
    return WALL_ORDER.some((wall) => validationErrors[wall].length > 0)
  }, [validationErrors])

  /** Add a new door to a wall */
  const addDoor = (wall: DoorWall) => {
    const wallLength = getWallLength(wall)
    const defaultDoorWidth = 60
    const newDoor: DoorSpec = {
      // Edge-based offset: door left edge at this position, centered on wall
      offset_ft: (wallLength - defaultDoorWidth) / 2,
      width_ft: defaultDoorWidth,
      height_ft: Math.min(20, heightFt - 2),
      type: 'bifold' as DoorType,
    }

    onChange({
      ...doors,
      [wall]: [...doors[wall], newDoor],
    })
  }

  /** Remove a door from a wall */
  const removeDoor = (wall: DoorWall, index: number) => {
    onChange({
      ...doors,
      [wall]: doors[wall].filter((_, i) => i !== index),
    })
  }

  /** Update one or more door properties atomically */
  const updateDoor = (
    wall: DoorWall,
    index: number,
    updates: Partial<DoorSpec>
  ) => {
    const updatedDoors = [...doors[wall]]
    updatedDoors[index] = { ...updatedDoors[index], ...updates }
    onChange({
      ...doors,
      [wall]: updatedDoors,
    })
  }

  /** Count total doors across all walls */
  const totalDoors = WALL_ORDER.reduce((sum, wall) => sum + doors[wall].length, 0)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
          <DoorOpen className="h-4 w-4" />
          Doors ({totalDoors})
          {hasErrors && (
            <AlertTriangle className="h-4 w-4 text-destructive" />
          )}
        </h4>
      </div>

      <Accordion type="multiple" className="w-full">
        {WALL_ORDER.map((wall) => {
          const wallErrors = validationErrors[wall]
          const hasWallErrors = wallErrors.length > 0

          return (
            <AccordionItem key={wall} value={wall}>
              <AccordionTrigger className="text-sm py-2">
                <div className="flex items-center gap-2">
                  <span>{WALL_LABELS[wall]}</span>
                  <span className="text-xs text-muted-foreground">
                    ({doors[wall].length} door{doors[wall].length !== 1 ? 's' : ''})
                  </span>
                  <span className="text-xs text-muted-foreground">
                    • {getWallLength(wall)} ft
                  </span>
                  {hasWallErrors && (
                    <AlertTriangle className="h-3 w-3 text-destructive" />
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  {/* Validation errors */}
                  {hasWallErrors && (
                    <div className="bg-destructive/10 border border-destructive/20 rounded-md p-2">
                      {wallErrors.map((error, i) => (
                        <p key={i} className="text-xs text-destructive flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {error}
                        </p>
                      ))}
                    </div>
                  )}

                  {doors[wall].map((door, index) => (
                    <DoorEditor
                      key={index}
                      door={door}
                      index={index}
                      wallLength={getWallLength(wall)}
                      maxHeight={heightFt}
                      onUpdate={(updates) => updateDoor(wall, index, updates)}
                      onRemove={() => removeDoor(wall, index)}
                    />
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addDoor(wall)}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Door
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          )
        })}
      </Accordion>
    </div>
  )
}
